import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { Gift, Coins, Loader2, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';

export default function RewardsPage() {
  const { api } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [learners, setLearners] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReward, setSelectedReward] = useState(null);
  const [selectedLearner, setSelectedLearner] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rewardsRes, learnersRes, redemptionsRes] = await Promise.all([
        api.get('/rewards/'),
        api.get('/learners/'),
        api.get('/rewards/redemptions/'),
      ]);
      // Handle both paginated (Django) and non-paginated responses
      setRewards(rewardsRes.data?.results || rewardsRes.data || []);
      setLearners(learnersRes.data?.results || learnersRes.data || []);
      setRedemptions(redemptionsRes.data?.results || redemptionsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!selectedLearner || !selectedReward) return;

    const learner = learners.find(l => l.id === selectedLearner);
    if (learner.points_balance < selectedReward.points_cost) {
      toast.error('Insufficient points');
      return;
    }

    setRedeeming(true);
    try {
      await api.post('/rewards/redeem/', {
        reward_id: selectedReward.id,
        learner_id: selectedLearner,
      });
      toast.success('Reward redeemed successfully!');
      setSelectedReward(null);
      await fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to redeem reward';
      toast.error(message);
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rewards-page">
      <div>
        <h1 className="font-heading text-2xl font-bold">Rewards Store</h1>
        <p className="text-muted-foreground">Redeem points for awesome rewards</p>
      </div>

      {/* Rewards Grid */}
      {rewards.length === 0 ? (
        <Card className="card-dashboard">
          <CardContent className="py-16 text-center">
            <Gift className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No rewards available</h2>
            <p className="text-muted-foreground">Check back soon for new rewards!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rewards.map((reward, index) => (
            <Card key={reward.id} className="card-marketing overflow-hidden" data-testid={`reward-${index}`}>
              <div className="h-48 bg-muted flex items-center justify-center">
                {reward.image_url ? (
                  <img src={reward.image_url} alt={reward.name} className="w-full h-full object-cover" />
                ) : (
                  <Gift className="h-16 w-16 text-muted-foreground" />
                )}
              </div>
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-2">{reward.name}</h3>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {reward.description}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-secondary font-bold">
                    <Coins className="h-5 w-5" />
                    {reward.points_cost} pts
                  </div>
                  <Badge variant={reward.inventory_qty > 0 ? 'secondary' : 'destructive'}>
                    {reward.inventory_qty > 0 ? `${reward.inventory_qty} left` : 'Out of stock'}
                  </Badge>
                </div>
                <Button 
                  className="w-full mt-4"
                  disabled={reward.inventory_qty === 0}
                  onClick={() => setSelectedReward(reward)}
                  data-testid={`redeem-btn-${index}`}
                >
                  Redeem
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Redemption History */}
      {redemptions.length > 0 && (
        <Card className="card-dashboard">
          <CardContent className="p-6">
            <h2 className="font-heading text-xl font-bold mb-4">Your Redemptions</h2>
            <div className="space-y-3">
              {redemptions.map((redemption, index) => (
                <div 
                  key={redemption.id}
                  className="flex items-center justify-between p-4 rounded-xl bg-muted/50"
                  data-testid={`redemption-${index}`}
                >
                  <div>
                    <p className="font-medium">Reward #{redemption.reward_id?.slice(0, 8)}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(redemption.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge className="capitalize">{redemption.status}</Badge>
                    <p className="text-sm text-muted-foreground mt-1">
                      -{redemption.points_spent} pts
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Redeem Dialog */}
      <Dialog open={!!selectedReward} onOpenChange={() => setSelectedReward(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Redeem Reward</DialogTitle>
          </DialogHeader>
          {selectedReward && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-muted/50">
                <h3 className="font-semibold">{selectedReward.name}</h3>
                <p className="text-sm text-muted-foreground">{selectedReward.description}</p>
                <p className="text-secondary font-bold mt-2">
                  <Coins className="h-4 w-4 inline mr-1" />
                  {selectedReward.points_cost} points
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Select Learner</label>
                <Select value={selectedLearner} onValueChange={setSelectedLearner}>
                  <SelectTrigger data-testid="redeem-learner-select">
                    <SelectValue placeholder="Choose learner" />
                  </SelectTrigger>
                  <SelectContent>
                    {learners.map(learner => (
                      <SelectItem key={learner.id} value={learner.id}>
                        {learner.first_name} {learner.last_name} ({learner.points_balance || 0} pts)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-4">
                <Button variant="outline" className="flex-1" onClick={() => setSelectedReward(null)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1" 
                  onClick={handleRedeem}
                  disabled={!selectedLearner || redeeming}
                  data-testid="confirm-redeem-btn"
                >
                  {redeeming && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Confirm Redemption
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
